import { useState, useEffect, useCallback } from 'react';
import { useUrlParams } from '../../../hooks/useUrlParams';
import { apiClient } from '../../../core/api/apiClient';
import { readListCache, writeListCache } from '../../../utils/listCache';

interface PaymentsResponse {
  payments?: PaymentData[];
  totalPages?: number;
  total?: number;
}

export interface PaymentData {
  payment_id: string;
  donor_id: string;
  donor_name: string;
  amount: number | string;
  closure_date?: string | { $date: string } | Date;
  stage?: string;
  probability?: number;
  donor_category?: string;
  receipt_category?: string;
  currency: string;
  date: string;
  project: string;
  paid?: boolean;
  donation_through?: string;
  type?: string;
  salesforce_object?: string;
  last_modified_date?: string | Date;
  _id?: string;
}

/** Params omitted from the URL when they hold these values, to keep links tidy. */
const PARAM_DEFAULTS: Record<string, string> = {
  page: '1',
  sort_by: 'date',
  sort_order: '-1',
};

export interface UsePaymentsReturn {
  searchTerm: string;
  startDate: string;
  endDate: string;
  payments: PaymentData[];
  loading: boolean;
  error: string | null;
  page: number;
  setPage: (page: number) => void;
  totalPages: number;
  totalItems: number;
  sortBy: string;
  sortOrder: number;
  handleSearchChange: (value: string) => void;
  handleStartDateChange: (value: string) => void;
  handleEndDateChange: (value: string) => void;
  handleSort: (field: string) => void;
}

export function usePayments(limit = 50): UsePaymentsReturn {
  // Kept in the URL so the list survives opening a donor's profile and coming back —
  // see useUrlParams.
  const { get, patch } = useUrlParams(PARAM_DEFAULTS);

  const searchTerm = get('q');
  const startDate = get('start_date');
  const endDate = get('end_date');
  const page = Number(get('page')) || 1;
  const sortBy = get('sort_by');
  const sortOrder = Number(get('sort_order')) === 1 ? 1 : -1;

  const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);

  const params: Record<string, string | number> = {
    page,
    limit,
    sort_by: sortBy,
    sort_order: sortOrder,
  };
  if (debouncedSearch) params.q = debouncedSearch;
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;

  const cacheKey = `payments-list:${JSON.stringify(params)}`;

  const [fetched, setFetched] = useState<{ key: string; data: PaymentsResponse } | null>(null);
  const [failure, setFailure] = useState<{ key: string; message: string } | null>(null);

  // Derived rather than copied into state, so returning from a donor's profile paints
  // the rows on the first render — see useAlumni for the reasoning.
  const data = fetched?.key === cacheKey ? fetched.data : readListCache<PaymentsResponse>(cacheKey);
  const error = failure?.key === cacheKey ? failure.message : null;

  const payments = data?.payments ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalItems = data?.total ?? 0;
  const loading = !data && !error;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    const hadRows = readListCache<PaymentsResponse>(cacheKey) !== undefined;

    const fetchPayments = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) throw new Error('No authentication token found');

        const result = await apiClient('/dashboard/alumni/payments', {
          method: 'GET',
          params
        });

        writeListCache(cacheKey, result);
        if (!cancelled) setFetched({ key: cacheKey, data: result });
      } catch (err) {
        console.error('Error fetching payments:', err);
        if (cancelled) return;
        // Don't let a failed background refresh replace good rows with an error.
        if (!hadRows) setFailure({ key: cacheKey, message: err instanceof Error ? err.message : 'An error occurred' });
      }
    };

    fetchPayments();
    return () => { cancelled = true; };
    // `params` is rebuilt every render; cacheKey is its stable identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey]);

  const setPage = useCallback((value: number) => patch({ page: value }), [patch]);

  const handleSearchChange = (value: string) => {
    patch({ q: value, page: 1 }); // Reset page on new search
  };

  const handleStartDateChange = (value: string) => {
    patch({ start_date: value, page: 1 });
  };

  const handleEndDateChange = (value: string) => {
    patch({ end_date: value, page: 1 });
  };

  const handleSort = (field: string) => {
    // Same field toggles direction; a new field starts descending.
    const nextOrder = sortBy === field && sortOrder === -1 ? 1 : -1;
    patch({ sort_by: field, sort_order: nextOrder, page: 1 });
  };

  return {
    searchTerm,
    startDate,
    endDate,
    payments,
    loading,
    error,
    page,
    setPage,
    totalPages,
    totalItems,
    sortBy,
    sortOrder,
    handleSearchChange,
    handleStartDateChange,
    handleEndDateChange,
    handleSort,
  };
}
